import 'normalize.css';
import '~/styles/main.scss';

import { Footer } from '@design-system/footer';
import { PageHeader } from '@design-system/page-header-standard';
import classNames from 'classnames';
import React, { FC, Suspense, useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet';

import ExternalLinkDialog from '~/common/ExternalLinkDialog';
import PageSchema from '~/common/PageSchema';
import Seo from '~/common/Seo';
import HeaderWrap from '~/components/HeaderWrap';
import HeaderWrapWithProfile from '~/components/HeaderWrapWithProfile';
import { ExternalLinkDialogContext } from '~/context/ExternalLinkDialogContext';
import { useContentDirection } from '~/hooks/useContentDirection';
import { useCurrentLang } from '~/hooks/useCurrentLang';
import { useLanguageSwitch } from '~/hooks/useLanguageSwitch';
import { footerMapper } from '~/mappers/footer';
import { pageHeaderMapper } from '~/mappers/pageHeader';
import { importAuthComponent } from '~/utils/importAuth';

import { LayoutProps } from './models';

const Auth = importAuthComponent(() => import('~/layout/Auth'));

export const Layout: FC<LayoutProps> = ({
  children,
  className,
  seo,
  pageSchemaType,
  header,
  footer,
  pageUid,
  locale,
  brand,
  pageHeaderProps,
  fullPath,
}) => {
  const CSSclassNames = classNames('layout', className);
  const { setCurrentLang } = useContext(ExternalLinkDialogContext);
  const { currentLang } = useCurrentLang();

  useEffect(() => {
    setCurrentLang(currentLang);
  }, [currentLang, setCurrentLang]);

  const languageSwitch = useLanguageSwitch(brand);
  const contentDirection = useContentDirection();

  const HeaderComponent = header?.profile?.length
    ? HeaderWrapWithProfile
    : HeaderWrap;

  return (
    <Suspense fallback={null}>
      <Auth>
        <ExternalLinkDialog />
        <div className={CSSclassNames}>
          <Helmet htmlAttributes={{ dir: contentDirection }} />
          {seo ? (
            <>
              <Seo
                {...{
                  seo,
                  urls: languageSwitch.list,
                  pageUid,
                  locale,
                  fullPath,
                }}
              />
              <PageSchema
                type={pageSchemaType || 'WebPage'}
                name={seo.title}
                data={{
                  metaTitle: seo.title,
                  metaDescription: seo.description,
                  metaKeywords: seo.keywords as string[] | null,
                }}
                locale={locale}
              />
            </>
          ) : null}

          {header ? (
            <HeaderComponent {...header} languageSwitch={languageSwitch} />
          ) : null}

          <main id="main" className="layout__main-wrapper">
            {!!pageHeaderProps?.pageHeader?.headingBlock?.headline ||
            pageHeaderProps?.pageHeader?.showBreadcrumbs ? (
              <PageHeader {...pageHeaderMapper({ ...pageHeaderProps })} />
            ) : null}
            <div className="layout__main">{children}</div>
          </main>

          {footer ? (
            <Footer {...footerMapper(footer)} className="footer" />
          ) : null}
        </div>
      </Auth>
    </Suspense>
  );
};
